import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Slide, Fade } from "@mui/material";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { loadColumnPreference } from "../../../common/CustomColumn/customize_column";
import { Container } from "react-bootstrap";
import ListHeading from "../../../common/RDTListHeading/ListHeading";
import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import Images from "../../../../utils/images";
import DataTable from "react-data-table-component";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { getCloudScanHistory, getScanResults } from "../../../../api/cloudDiscovery";
import { showError } from "../../../../utils/showMessage";
import { RiScanLine } from "react-icons/ri";
import FirstPageIcon from "../../../../images/first_page.svg";
import LastPageIcon from "../../../../images/last_page.svg";
import NextPageIcon from "../../../../images/arrow_right.svg";
import PreviousPageIcon from "../../../../images/arrow_left.svg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import SubHeaderComponent from "../../../common/RDTSubHeaderComponent/SubHeaderComponent";
import "../CloudConfiguration.scss";
import { BsInfoCircle } from "react-icons/bs";
import { Tooltip } from "@mui/material";
import DataTableSkeleton from "../../../common/Skeletons/DataTableSkeleton";
import CustomDelaySkeleton from "../../../common/DelaySkeleton/CustomDelaySkeleton";
import GetCapabilities from "../../../../utils/getCapabilities.js";
import { PutColumnPreference } from "../../../../api/column_pref";
import { useTranslation } from "react-i18next";
export default function ScanHistory() {
  const { t } = useTranslation();
  const { config_id, id } = useParams();
  const configId = config_id || id;
  const navigate = useNavigate();
  const { height } = useWindowDimensions();
  const my_capabilities = GetCapabilities();
  const isCapable = my_capabilities[0];

  const [slideChecked, setSlideChecked] = useState(false);
  const [pending, setPending] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filterBy, setFilterBy] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const tableHeightWithData = height - 220 + "px";
  const tableHeightEmpty = height - 220 + "px";
  const [datatableHeight, setDatatableHeight] = useState(tableHeightEmpty);
  const [list, setList] = useState([]);
  const pages_url = "dashboard/discovery/cloud_scan_history";

  const customizeColumnDict = {
    "reports.status": true,
    "recordedSessions.startTime": true,
    "cloudDiscovery.endTime": true,
    "cloudDiscovery.duration": true,
    "cloudDiscovery.totalResources": true,
  };
  const [checkedStateDict, setCheckedStateDict] = useState(customizeColumnDict);
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  const [colCount, setColCount] = useState(null);
  const filterOptions = [
    { label: t("common.status"), value: "status" },
    { label: t("cloudDiscovery.startTime"), value: "started_at" },
    { label: t("cloudDiscovery.endTime"), value: "completed_at" },
    { label: t("cloudDiscovery.duration"), value: "duration" },
    { label: t("cloudDiscovery.totalResources"), value: "total_resources" },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) {
      setFilteredData(list);
    } else {
      const lowerSearch = search.toLowerCase();
      const filtered = list.filter(row => {
        const resourcesByTypeStr = row.resources_by_type 
          ? Object.entries(row.resources_by_type).map(([type, count]) => `${type}:${count}`).join(" ")
          : "";
        return (
          String(row.status || "").toLowerCase().includes(lowerSearch) ||
          String(row.started_at || "").toLowerCase().includes(lowerSearch) ||
          String(row.completed_at || "").toLowerCase().includes(lowerSearch) ||
          String(row.duration || "").toLowerCase().includes(lowerSearch) ||
          String(row.total_resources || "").toLowerCase().includes(lowerSearch) ||
          resourcesByTypeStr.toLowerCase().includes(lowerSearch)
        );
      });
      setFilteredData(filtered);
    }
  };

  const getList = async ({ page, rowsPerPage, search, filterBy }) => {
    const { data } = await getCloudScanHistory({
      page,
      page_size: rowsPerPage,
      search,
      filter_by: filterBy,
    });
    if (data) {
      setList(data.results);
      setFilteredData(data.results);
      setTotal(data.count);
    }
    setPending(false);
  };
  useEffect(() => {
    loadColumnPreference(setCheckedStateDict, pages_url, checkedStateDict, rowsPerPage, setRowsPerPage, setPreferenceLoaded);
  }, []);

  useEffect(() => {
    if (!preferenceLoaded) return;
    setPending(true);
    fetchScanHistory();
  }, [page, rowsPerPage, configId, preferenceLoaded]);


  const fetchScanHistory = async () => {
    if (!configId) {
      setPending(false);
      return;
    }
    
    setPending(true);
    try {
      const { data, error } = await getCloudScanHistory({
        config_id: configId,
        page: page - 1,
        page_size: rowsPerPage,
      });
      
      if (error) {
        showError(error?.message?.[0] || error?.message || "Failed to fetch scan history");
        setList([]);
        setFilteredData([]);
        setTotal(0);
        setPending(false);
        return;
      }
      
      if (!data) {
        setList([]);
        setFilteredData([]);
        setTotal(0);
        setPending(false);
        return;
      }
      
      const rawScans = data?.results || data?.scans || [];
      
      if (!Array.isArray(rawScans)) {
        setList([]);
        setFilteredData([]);
        setTotal(0);
        setPending(false);
        return;
      }
      
      if (rawScans.length === 0) {
        setList([]);
        setFilteredData([]);
        setTotal(0);
        setPending(false);
        return;
      }
      
      const scans = rawScans.map(scan => {
        let duration = "-";
        if (scan.started_at && scan.completed_at) {
          const start = new Date(scan.started_at);
          const end = new Date(scan.completed_at);
          const diffMs = end - start;
          const diffSeconds = Math.floor(diffMs / 1000);
          const diffMinutes = Math.floor(diffSeconds / 60);
          const diffHours = Math.floor(diffMinutes / 60);
          
          if (diffHours > 0) {
            duration = `${diffHours}h ${diffMinutes % 60}m`;
          } else if (diffMinutes > 0) {
            duration = `${diffMinutes}m ${diffSeconds % 60}s`;
          } else {
            duration = `${diffSeconds}s`;
          }
        } else if (scan.started_at && scan.status === "IN_PROGRESS") {
          const start = new Date(scan.started_at);
          const now = new Date();
          const diffMs = now - start;
          const diffSeconds = Math.floor(diffMs / 1000);
          const diffMinutes = Math.floor(diffSeconds / 60);
          const diffHours = Math.floor(diffMinutes / 60);
          
          if (diffHours > 0) {
            duration = `${diffHours}h ${diffMinutes % 60}m (running)`;
          } else if (diffMinutes > 0) {
            duration = `${diffMinutes}m ${diffSeconds % 60}s (running)`;
          } else {
            duration = `${diffSeconds}s (running)`;
          }
        }
        
        const resourcesByType = scan.summary?.by_type || {};
        const warnings = scan.summary?.warnings || [];
        const warningsCount = Array.isArray(warnings) ? warnings.length : 0;
        
        return {
          ...scan,
          status: scan.status || "-",
          started_at: scan.started_at ? new Date(scan.started_at).toLocaleString() : "-",
          completed_at: scan.completed_at ? new Date(scan.completed_at).toLocaleString() : "-",
          total_resources: scan.total_resources || 0,
          duration: duration,
          resources_by_type: resourcesByType,
          warnings_count: warningsCount,
          warnings: warnings,
        };
      });
      
      setList(scans);
      setFilteredData(scans);
      setTotal(data?.count || data?.total || scans.length);
    } catch (err) {
      showError("An unexpected error occurred while fetching scan history");
      setList([]);
      setFilteredData([]);
      setTotal(0);
    } finally {
      setPending(false);
    }
  };

  const handlePageChange = (p) => setPage(p);
  const handlePerRowsChange = async (newPerPage) => {
    setPage(1);
    setRowsPerPage(newPerPage);
    await PutColumnPreference(checkedStateDict, pages_url, newPerPage);
  };

  const formatScanStatus = (status) => {
    if (!status) return { text: "-", class: "" };
    
    const upperStatus = status.toUpperCase();
    let statusText = status;
    let statusClass = "";
    
    if (upperStatus === "COMPLETED") {
      statusText = t("cloudDiscovery.completed");
      statusClass = "cloud-discovery-status-badge cloud-discovery-status-active";
    } else if (upperStatus === "FAILED") {
      statusText = t("cloudDiscovery.failed");
      statusClass = "cloud-discovery-status-badge cloud-discovery-status-inactive";
    } else if (upperStatus === "IN_PROGRESS" || upperStatus === "PENDING") {
      statusText = upperStatus === "IN_PROGRESS" ? t("cloudDiscovery.inProgress") : t("cloudDiscovery.pending");
        statusClass = "cloud-discovery-status-badge cloud-discovery-status-partially-active";
    } else {
      statusText = status;
      statusClass = "cloud-discovery-status-badge cloud-discovery-status-partially-active";
    }
    
    return { text: statusText, class: statusClass };
  };

  const columns = useMemo(() => {
    const cols = [
    {
      name: t("common.status"),
      cell: (row) => {
        const { text, class: statusClass } = formatScanStatus(row.status);
        const warnings = row.warnings || [];
        const warningsCount = row.warnings_count || 0;
        
        const tooltipContent = warningsCount > 0 && warnings.length > 0 ? (
          <div className="py-1 cloud-discovery-tooltip-content">
            <strong className="d-block mb-2">Warnings ({warningsCount}):</strong>
            <ul className="m-0 ps-4 small">
              {warnings.map((warning, index) => (
                <li key={index} className="mb-1">
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        ) : warningsCount > 0 ? `${warningsCount} warning(s) found` : null;
        
        return (
          <div className="d-flex align-items-center justify-content-between w-100">
            <span className={`${statusClass} fw-semibold`}>
              {text}
            </span>
            {warningsCount > 0 && (
              <div className="d-flex align-items-center gap-2 ms-auto">
                <Tooltip 
                  title={tooltipContent} 
                  arrow 
                  placement="top"
                  enterDelay={200}
                  leaveDelay={100}
                >
                  <span className="d-inline-flex align-items-center">
                    <BsInfoCircle 
                      className="cloud-discovery-info-icon"
                    />
                  </span>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
      selector: (row) => row.status || "-",
      grow: 1,
      sortable: true,
      omit: checkedStateDict["reports.status"] === false,
    },
    {
      name: t("cloudDiscovery.startTime"),
      selector: (row) => row.started_at || "-",
      grow: 1,
      sortable: true,
      omit: checkedStateDict["recordedSessions.startTime"] === false,
    },
    {
      name: t("cloudDiscovery.duration"),
      selector: (row) => row.duration || "-",
      grow: 1,
      sortable: true,
      omit: checkedStateDict["cloudDiscovery.duration"] === false,
    },
    {
      name: t("cloudDiscovery.endTime"),
      selector: (row) => row.completed_at || "-",
      grow: 1,
      sortable: true,
      omit: checkedStateDict["cloudDiscovery.endTime"] === false,
    },
    {
      name: t("cloudDiscovery.totalResources"),
      cell: (row) => {
        const totalResources = row.total_resources || 0;
        const resourcesByType = row.resources_by_type || {};
        const entries = Object.entries(resourcesByType);
        
        const tooltipContent = entries.length > 0 ? (
          <div className="py-1 cloud-discovery-tooltip-content">
            <strong className="d-block mb-2">Resources by Type:</strong>
            <ul className="m-0 ps-4 small">
              {entries.map(([type, count], index) => (
                <li key={index} className="mb-1">
                  {type}: {count}
                </li>
              ))}
            </ul>
          </div>
        ) : t("cloudDiscovery.noResourceTypeBreakdown");
        
        return (
          <Tooltip 
            title={tooltipContent} 
            arrow 
            placement="top"
            enterDelay={200}
            leaveDelay={100}
          >
            <span>
              {totalResources}
            </span>
          </Tooltip>
        );
      },
      selector: (row) => row.total_resources || 0,
      grow: 1,
      sortable: true,
      omit: checkedStateDict["cloudDiscovery.totalResources"] === false,
    },
    ];
    
    if (isCapable?.cloud_discovery?.view_scan_results) {
      cols.push({
        name: t("common.actions"),
        cell: (row) => (
          <div
            role="button"
            className="text-decoration-none text-primary d-flex align-items-center"
            onClick={() => navigate(`/dashboard/discovery/cloudconfiguration/history/${configId}/results/${row.id}`)}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center">
              <RiScanLine />
            </div>
            <div className="ps-1">{t("cloudDiscovery.viewResults")}</div>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        minWidth: "120px",
      });
    }
    
    return cols;
  }, [checkedStateDict, isCapable, navigate, configId]);

  useEffect(() => {
    const div = document.getElementById("cloudDatatableDivId");
    if (div && list && list.length > 0) {
      div.style.height = tableHeightWithData;
      setDatatableHeight(tableHeightWithData);
    } else {
      setDatatableHeight(tableHeightEmpty);
    }
  }, [list]);

  useEffect(() => {
    if (!list) return;
    const s = search.replaceAll(/[\\*()+[?]/g, "");
    const result = list.filter((o) => {
      if (!filterBy) {
        return (
          String(o.id || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.status || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.started_at || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.completed_at || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.duration || "").toLowerCase().includes(s.toLowerCase()) ||
          String(o.total_resources || "").toLowerCase().includes(s.toLowerCase())
        );
      }
      return String(o[filterBy] || "").toLowerCase().includes(s.toLowerCase());
    });
    setFilteredData(result);
  }, [search, list, filterBy]);

  useEffect(() => {
    document.title = "Scan History";
    setSlideChecked(true);
  }, []);

  useEffect(() => {
    fetchScanHistory();
  }, [configId, page, rowsPerPage]);

  const updateInProgressScans = async () => {
    if (!configId) return;

    const inProgressScans = list.filter(scan => {
      const status = scan.status?.toUpperCase();
      return status === "IN_PROGRESS" || status === "PENDING";
    });

    if (inProgressScans.length === 0) {
      return;
    }

    try {
      const updatePromises = inProgressScans.map(async (scan) => {
        try {
          const { data, error } = await getCloudScanHistory({
            scan_id: scan.id,
            config_id: configId,
          });

          if (error || !data?.scan) {
            return null;
          }

          const updatedScan = data.scan;
          
          let duration = "-";
          if (updatedScan.started_at && updatedScan.completed_at) {
            const start = new Date(updatedScan.started_at);
            const end = new Date(updatedScan.completed_at);
            const diffMs = end - start;
            const diffSeconds = Math.floor(diffMs / 1000);
            const diffMinutes = Math.floor(diffSeconds / 60);
            const diffHours = Math.floor(diffMinutes / 60);
            
            if (diffHours > 0) {
              duration = `${diffHours}h ${diffMinutes % 60}m`;
            } else if (diffMinutes > 0) {
              duration = `${diffMinutes}m ${diffSeconds % 60}s`;
            } else {
              duration = `${diffSeconds}s`;
            }
          } else if (updatedScan.started_at && updatedScan.status === "IN_PROGRESS") {
            const start = new Date(updatedScan.started_at);
            const now = new Date();
            const diffMs = now - start;
            const diffSeconds = Math.floor(diffMs / 1000);
            const diffMinutes = Math.floor(diffSeconds / 60);
            const diffHours = Math.floor(diffMinutes / 60);
            
            if (diffHours > 0) {
              duration = `${diffHours}h ${diffMinutes % 60}m (running)`;
            } else if (diffMinutes > 0) {
              duration = `${diffMinutes}m ${diffSeconds % 60}s (running)`;
            } else {
              duration = `${diffSeconds}s (running)`;
            }
          }

          const resourcesByType = updatedScan.summary?.by_type || {};
          const warnings = updatedScan.summary?.warnings || [];
          const warningsCount = Array.isArray(warnings) ? warnings.length : 0;

          return {
            ...updatedScan,
            status: updatedScan.status || "-",
            started_at: updatedScan.started_at ? new Date(updatedScan.started_at).toLocaleString() : "-",
            completed_at: updatedScan.completed_at ? new Date(updatedScan.completed_at).toLocaleString() : "-",
            total_resources: updatedScan.total_resources || 0,
            duration: duration,
            resources_by_type: resourcesByType,
            warnings_count: warningsCount,
            warnings: warnings,
          };
        } catch (err) {
          return null;
        }
      });

      const updatedScans = await Promise.all(updatePromises);
      const validUpdates = updatedScans.filter(scan => scan !== null);
      
      if (validUpdates.length > 0) {
        setList(prevList => {
          const updatedList = prevList.map(scan => {
            const updatedScan = validUpdates.find(updated => updated.id === scan.id);
            return updatedScan || scan;
          });
          return updatedList;
        });

        setFilteredData(prevFiltered => {
          const updatedFiltered = prevFiltered.map(scan => {
            const updatedScan = validUpdates.find(updated => updated.id === scan.id);
            return updatedScan || scan;
          });
          return updatedFiltered;
        });
      }
    } catch (err) {
      console.error("Error updating in-progress scans:", err);
    }
  };

  useEffect(() => {
    const hasInProgressScans = list.some(scan => {
      const status = scan.status?.toUpperCase();
      return status === "IN_PROGRESS" || status === "PENDING";
    });

    let intervalId = null;

    if (hasInProgressScans && configId) {
      intervalId = setInterval(() => {
        updateInProgressScans();
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [list, configId]);
  return (
    <>
      {pending && list.length === 0 ? (
        <DataTableSkeleton rowCount={7} columnCount={8} />
      ) : (
        <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
          <Fade timeout={fadedelayTime} in={slideChecked}>
          <div className="main_content_container p-3 w-100">
            <div
              className="position-relative bg-white"
              style={{ height: datatableHeight }}
            >
              {!preferenceLoaded ? (
                <CustomDelaySkeleton
                  rowsPerPage={rowsPerPage}
                  colCount={0}
                  totalColumns={8}
                />
              ) : (
              <DataTable
              title={
                <>
                  <div className="py-2 pt-2">
                    <BreadcrumbHeading
                      dataTableHeadingText={[
                        t("cloudDiscovery.backToCloudDiscovery"),
                        t("cloudDiscovery.scanHistory"),
                      ]}
                      dataTablePageLink={[
                        "/dashboard/discovery/cloudconfiguration",
                        `/dashboard/discovery/cloudconfiguration/history/${configId}`,
                      ]}
                      pageNumber={4}
                    />
                  </div>
                  <div className="mb-3">
                    <ListHeading
                      dataTableHeadingText={t("cloudDiscovery.scanHistory")}
                      dataTableHeadingIcon={Images.NetworkAnalyzer}
                    />
                  </div>
                </>
              }
              columns={columns}
              data={filteredData || []}
              className="rdt_container"
              progressPending={pending}
              progressComponent={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                  <div style={{ color: '#5177ff' }} className="la-line-scale-pulse-out-rapid la-3x">
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                    <div className="rounded"></div>
                  </div>
                </div>
              }
              noDataComponent={t("cloudDiscovery.noScanHistoryFound")}
              fixedHeader
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
              pagination
              paginationServer
              paginationTotalRows={total}
              highlightOnHover
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              subHeader
              subHeaderComponent={
                <SubHeaderComponent
                  filterBy={filterBy}
                  setFilterBy={setFilterBy}
                  filterOptions={filterOptions}
                  search={search}
                  setSearch={setSearch}
                  handleSearch={handleSearch}
                  placeholder="Scan History"
                  pages_url={pages_url}
                  checkedStateDict={checkedStateDict}
                  setCheckedStateDict={setCheckedStateDict}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  onRefresh={() => {
                    setPending(true);
                    fetchScanHistory();
                  }}
                />
              }
              subHeaderAlign="center"
              paginationIconFirstPage={
                <LazyLoadImage src={FirstPageIcon} alt="first page" />
              }
              paginationIconLastPage={
                <LazyLoadImage src={LastPageIcon} alt="last page" />
              }
              paginationIconNext={
                <LazyLoadImage src={NextPageIcon} alt="next" />
              }
              paginationIconPrevious={
                <LazyLoadImage src={PreviousPageIcon} alt="previous" />
              }
            />
              )}
            </div>
          </div>
        </Fade>
      </Slide>
      )}
    </>
  );
}

